import { useMusicalSelector } from "../TrainingConfiguration";
import { mainActions } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import BottomMenu from "../BottomMenu/BottomMenu";
import Card from "../UI/Card";
import { ChartDataToFretboardSVG } from "../code/ChartDataToSVG";
import classes from "./FretboardPage.module.css";
import { isSupportedAudioPitch } from "../EarTrainerPage/audioPitch";
import NotePlayer from "../EarTrainerPage/NotePlayer";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import FretboardSetup from "./FretboardSetup/FretboardSetup";
import FretboardChart from "./FretboardChart";
import FretboardQuickTour from "./FretboardQuickTour";
import { createQuestions, fretCount, notePitch, matchesQuestion, diagramSpelling } from "./trainingModel";
import RequestSupportCard from "../UI/RequestSupportCard";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Link } from "react-router-dom";

export const ANSWER_CONFIRMATION_MS = 300;

const FretboardPage = () => {
  const dispatch = useDispatch();
  const fretboardNotesDisplay = useSelector(state => state.fretboardNotesDisplay);
  const tuning = useMusicalSelector(state => state.tuning);
  const midiRootNote = useMusicalSelector(state => state.midiRootNote);
  const useSharps = useSelector(state => state.fretboardUseSharps);
  const soundEnabled = useSelector(state => state.fretboardSoundEnabled);
  const showNoteNames = useSelector(state => state.fretboardShowNoteNames);
  const lowestFret = 0;
  const maxFret = fretCount(tuning);
  const configuration = useMemo(() => ({
    tuning, midiRootNote,
    questions: createQuestions(fretboardNotesDisplay, useSharps),
  }), [fretboardNotesDisplay, useSharps, tuning, midiRootNote]);
  const [queueState, setQueueState] = useState(() => ({ configuration, questions: configuration.questions }));
  const questionList = queueState.configuration === configuration ? queueState.questions : configuration.questions;
  const [trainingRequested, setTrainingRequested] = useState(false);
  const isTraining = trainingRequested && questionList.length > 0;
  const question = questionList[0];
  const handledQuestion = useRef(null);
  const confirmation = useRef(null);
  const feedbackTimeout = useRef(null);
  const pending = useRef(null);
  const [feedback, setFeedback] = useState(null);
  const audio = useRef(null);
  const [audioState, setAudioState] = useState({ status: "loading", message: "Preparing Fretboard Trainer audio…" });
  const [playbackNotice, setPlaybackNotice] = useState("");
  const mounted = useRef(false);
  const playbackGeneration = useRef(0);
  const labelSpelling = diagramSpelling(useSharps, isTraining, question);
  const svgData = useMemo(() => ChartDataToFretboardSVG(tuning, midiRootNote,
    lowestFret, maxFret, fretboardNotesDisplay, showNoteNames, labelSpelling),
  [tuning, midiRootNote, maxFret, fretboardNotesDisplay, showNoteNames, labelSpelling]);

  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useLayoutEffect(() => {
    setQueueState(current => current.configuration === configuration ? current
      : { configuration, questions: configuration.questions });
    if (!configuration.questions.length) setTrainingRequested(false);
    playbackGeneration.current++;
    setPlaybackNotice("");
    setFeedback(null);
    return () => {
      clearTimeout(confirmation.current);
      clearTimeout(feedbackTimeout.current);
      pending.current = null;
    };
  }, [configuration, isTraining]);
  // A synchronous guard covers repeated events before React commits the new queue.
  // Release on that commit, with no timed lock between ordinary questions.
  useLayoutEffect(() => {
    handledQuestion.current = null;
    setFeedback(null);
  }, [questionList, isTraining]);

  const onAudioStateChange = useCallback(state => {
    setAudioState({ ...state, message: state.message?.replaceAll("Ear Trainer", "Fretboard Trainer") });
  }, []);
  const onSoundChange = enabled => {
    // Invalidate a pending activation before releasing this page's player.
    playbackGeneration.current++;
    setPlaybackNotice("");
    setAudioState({ status: "loading", message: "Preparing Fretboard Trainer audio…" });
    dispatch(mainActions.setFretboardSoundEnabled(enabled));
  };
  const play = pitch => {
    if (!soundEnabled) return;
    setPlaybackNotice("");
    if (!isSupportedAudioPitch(pitch)) {
      setPlaybackNotice("This note is outside the supported audio range (MIDI 23–127).");
      return;
    }
    if (!["ready", "blocked"].includes(audioState.status)) return;
    const generation = playbackGeneration.current;
    const activated = audio.current?.activate(); // Directly in the user's gesture.
    const finish = ok => {
      if (ok && mounted.current && playbackGeneration.current === generation) audio.current?.play(pitch);
    };
    if (activated?.then) activated.then(finish);
    else finish(activated);
  };
  const handleNoteClick = (string, fret) => {
    if (!Number.isInteger(string) || string < 0 || string > tuning.length ||
      !Number.isInteger(fret) || fret < 0 || fret >= maxFret) return;
    const pitch = notePitch(tuning, midiRootNote, string, fret);
    if (!isTraining) { play(pitch); return "explore"; }
    if (handledQuestion.current === questionList) return;
    clearTimeout(feedbackTimeout.current);
    if (!matchesQuestion(pitch, question)) {
      setFeedback({ configuration, questions: questionList, result: "incorrect" });
      feedbackTimeout.current = setTimeout(() => setFeedback(null), 500);
      return "incorrect";
    }
    handledQuestion.current = questionList;
    setFeedback({ configuration, questions: questionList, result: "correct" });
    play(pitch);
    const token = {};
    pending.current = token;
    confirmation.current = setTimeout(() => {
      if (!mounted.current || pending.current !== token) return;
      pending.current = null;
      // Randomness stays outside React's replayable state updater.
      const questions = questionList.length > 1 ? questionList.slice(1)
        : createQuestions(fretboardNotesDisplay, useSharps, question);
      setQueueState({ configuration, questions });
    }, ANSWER_CONFIRMATION_MS);
    return "correct";
  };
  const activeFeedback = isTraining && feedback?.configuration === configuration &&
    feedback.questions === questionList ? feedback.result : null;
  const onTraining = () => setTrainingRequested(current => questionList.length > 0 && !current);

  const onShowNoteNames = () => {
    dispatch(mainActions.setFretboardShowNoteNames(!showNoteNames));
  };

  return (
    <div className={classes.background} data-fretboard-trainer-page>
      <HelmetProvider>
        <Helmet>
          <meta charset="UTF-8" />
          <title>Guitar Fretboard Trainer | Guitar Charts</title>
          <meta
            name="description"
            content="Learn note locations on the guitar fretboard. Find each named note on your instrument with adjustable note labels, sharp and flat spelling, and a six-stage practice guide."
          />
          <link rel="canonical" href="https://guitar-charts.com/fretboard" />
        </Helmet>
      </HelmetProvider>
      <Card className={classes.headingCard}>
        <h1>Guitar Charts - Fretboard Trainer</h1>
        <div className={classes.introduction}>
          <p>Learn where notes occur on your fretboard. The trainer gives you a note name;
            select a matching string and fret. Regular practice can help you become familiar
            with note locations across the neck.</p>
          <div className={classes.helpLinks}>
            <a href="#fretboard-usage">How to use</a>
            <FretboardQuickTour training={isTraining} />
          </div>
        </div>
      </Card>
      <RequestSupportCard />
      <Card>
        <FretboardSetup
          soundEnabled={soundEnabled}
          onSoundChange={onSoundChange}
          audio={audioState}
          playbackNotice={playbackNotice}
          onRetry={() => { playbackGeneration.current++; audio.current?.retry(); }}
          onResume={() => audio.current?.activate()}
          isTraining={isTraining}
          onTraining={onTraining}
          showNoteNames={showNoteNames}
          onShowNoteNames={onShowNoteNames}
        />
      </Card>
      <Card>
        <table>
          <tbody>
            <tr>
              <td style={{ textAlign: "center" }}>
                {isTraining ? "Select the requested note on the chart" : soundEnabled ? "Click on the chart below to play the notes" : "Select a position on the chart to explore"}
              </td>
            </tr>
            <tr>
              <td className={classes.td}>
                <span role="status" aria-label="Current question" aria-atomic="true">
                  {!questionList.length ? <b>Select at least one note to start training.</b>
                    : isTraining && <>Can you find a&nbsp;<b>{question?.[1]}</b>?</>}
                </span>
                &nbsp;
                <span role="status" aria-label="Answer feedback" className={classes.feedback}>
                  {activeFeedback === "correct" ? "Correct" : activeFeedback === "incorrect" ? "Try again" : ""}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
      <Card>
        <div className={classes.imageGrid} data-tour-target="fretboard-neck">
          <FretboardChart
            image={svgData.image}
            onClick={handleNoteClick}
            stringCount={tuning.length + 1}
            maxFret={maxFret}
            feedbackContext={questionList}
            trainingMode={isTraining}
            confirmationDuration={ANSWER_CONFIRMATION_MS}
          />
        </div>
      </Card>
      <Card>
        <div className={classes.articleDiv}>
          <h2 id="fretboard-usage">How to use the Fretboard Trainer</h2>
          <p>Choose your instrument and a scale shortcut or individual notes. Show note names
            while learning, then hide them to test your memory. Select <b>Start Training!</b>{" "}
            and find each requested note. Any matching note on the displayed fretboard is
            correct, on any string or octave: any C can answer a C question, even without a label.
            <b> Stop Training</b> returns to exploration, where selecting a position plays its pitch when Sound is On. Sound is optional;
            turn it Off to practise without audio.</p>
          <p><b>Use Flats</b> and <b>Use Sharps</b> choose the accidental names in questions and
            diagram labels. <b>Use Both</b> mixes sharp and flat questions; during training the
            current question determines the diagram's accidental spelling. Naturals and
            exploration use sharp labels in this mode.</p>
          <p>For keyboard practice, Tab to the fretboard, move with the arrow keys, then press
            Enter or Space to answer or play a note in exploration. String 1 is at the top;
            fret 0 is open. The short correct-answer confirmation finishes before another answer.</p>
          <p>The six stages below suggest areas to concentrate on voluntarily. The trainer
            accepts matching notes throughout the displayed neck; it does not restrict strings or frets.
            The string names and examples below describe standard EADGBE guitar tuning;
            adapt them for your instrument.</p>
          <h2>
            Mastering the Guitar Neck: <br />A Comprehensive Guide to <br />
            Learning All the Notes
          </h2>
          <h3>Stage 1 - The Open Strings:</h3>
          <p>
            Typically, you would leave the <b>"Guitar Setup"</b> as{" "}
            <b>"Standard 6 String Guitar eg. EADGBE"</b>, as that is the
            standard guitar layout. However, you can use this method for any
            instrument or tuning.
          </p>
          <p>
            For the <b>"Scale,"</b> choose <b>"Open Strings"</b>. If the notes
            are being shown, you should see circles to the left of the fret
            markings highlighted with the names of the open strings. This shortcut selects
            their note names throughout the neck, not just fret zero. For this stage,
            choose to answer on the open strings yourself.
          </p>
          <p>
            My favorite mnemonic when I was first learning was, "
            <i>
              <b>E</b>very <b>A</b>pple <b>D</b>oes <b>G</b>o <b>B</b>ad{" "}
              <b>E</b>ventually
            </i>
            ," which gives you the letters of the notes from the lowest string
            to the highest. There are many other mnemonics you can use as well.
          </p>
          <p>
            Now start training by clicking on <b>"Start Training!"</b> and don't
            forget to hide the notes by clicking on <b>"Hide Notes."</b>
          </p>
          <p>
            Focus on the open-string positions to the left of the nut and see if
            you can find the notes asked for just above the chart.
          </p>
          <p>
            A correct answer shows green feedback and, when Sound is On, plays the pitch you selected.
            After 300 milliseconds, the next question appears automatically. An incorrect
            answer shows red feedback and “Try again” without playing a note;
            you can retry immediately.
          </p>
          <p>
            Give yourself a little time to acclimate and start finding all the
            notes more readily.
          </p>
          <h3>Stage 2 - The E String</h3>
          <p>
            Set the <b>"Scale"</b> to be <b>"C Major/ A Minor"</b> and show all
            the notes by clicking <b>"Show Notes"</b> if they're not already
            visible.
          </p>
          <p>
            Notice that the note <b>E</b> first occurs on the open string of the
            E String and then occurs again at the 12th fret. This is because all
            of the notes are available at some point between the open fret and
            the 12th fret, and this is true for all the other strings too.
          </p>
          <p>
            Let's just focus on that low E string and see if we can find them.
          </p>
          <p>
            Again, hide the notes and start training if you haven't already. See
            if you can find all the notes as the questions arrive.
          </p>
          <p>
            If this feels too hard, you can always take a quick peek at where
            the notes are or even simplify things by removing some of the notes.
          </p>
          <p>
            As you get better, try adding some more notes until you can do the
            full Chromatic Scale.
          </p>
          <p>
            Don't worry if this takes a few sessions before it clicks. That's
            totally normal.
          </p>
          <p>
            You may find the sharps and flats particularly challenging. If so,
            try a few sessions focused on these. You can use the Scale{" "}
            <b>"Sharps or Flats"</b> to set things up quickly.
          </p>
          <h3>Stage 3 - The Other Strings</h3>
          <p>
            The guitar is tuned with 2 E strings. Not surprisingly, all the
            notes on the high E string are in an identical place to the low E
            string.
          </p>
          <p>
            It may take your brain a while to adjust, but ultimately, you should
            be able to find all the notes on the high E as easily as you can on
            the low E.
          </p>
          <p>
            So the next real challenge is the A string. The approach to the A
            string is the same as for the E string. Give yourself a little time,
            and pretty soon you'll get it too.
          </p>
          <p>
            Once you have that, move on to the others, or alternatively try{" "}
            <b>Stage 4</b> and come back later for the other strings.
          </p>
          <h3>Stage 4 - Open Strings to the 4th Fret</h3>
          <p>
            For this stage, concentrate on finding the notes from the open
            strings to the 4th fret.
          </p>
          <p>
            In standard guitar tuning, all the notes
            of the chromatic scale occur at least once across a span of 5 frets
            and across a group of 3 strings. We're going to take advantage of
            that for the next part of the training.
          </p>
          <p>
            Let's start with the <b>high E, B, and G strings</b> and just focus
            on the <b>open strings up to the 4th fret</b>.
          </p>
          <p>
            Again, you can simplify the scale if you want, but the goal is to
            build up to where you can find all of the notes of the Chromatic
            scale.
          </p>
          <p>
            Similarly, we can do the same thing with the bottom 3 strings, the{" "}
            <b>low E, A, and D strings</b>.
          </p>
          <p>
            Actually, you can take this same approach with any 3 consecutive
            strings, so why not test it out too? For example, try the{" "}
            <b>D, G and B strings</b> or the <b>A, D and G strings</b>, all from
            the <b>open strings up to the 4th fret</b>
          </p>
          <h3>Stage 5 - The 12th Fret</h3>
          <p>
            Another interesting property of the guitar is that everything
            repeats above the 12th fret. So all of the notes that you found in
            the open-to-fourth-fret region occur again from the 12th fret. Click{" "}
            <b>Show Notes</b>, and you should be able to see what I mean.
          </p>
          <p>
            Again, we're going to train in groups of 3 strings over a 5-fret
            span. Choose to answer only from the <b>12th to the
            16th frets</b> for this stage; the trainer still accepts matching notes elsewhere.
          </p>
          <p>
            Once you get the hang of it, you should find these start to come
            easily.
          </p>
          <h3>Stage 6 - The Other Frets</h3>
          <p>
            Now you're at the stage where you can test yourself on other fret
            ranges.
          </p>
          <p>
            Again, pick a set of 3 strings and a start and end fret. It doesn't
            really matter which ones, but if I were to recommend anything, I'd
            say:
          </p>
          <ul>
            <li>5th Fret to 9th Fret</li>
            <li>9th Fret to the 13th Fret</li>
            <li>3rd Fret to the 7th Fret</li>
          </ul>
          <p>
            At this point, you have all the basic knowledge you need, and from
            now on, you're just improving your fluency.
          </p>
          <h3>Final Thoughts</h3>
          <p>
            This kind of training is best done using lots of short sessions
            spread out over the weeks and months, rather than trying to cram
            everything into one huge session.
          </p>
          <p>
            Give yourself the time you need to achieve it, and you may be amazed
            at how quickly you can pick it up!
          </p>
          <p>
            <b>
              <i>Good Luck! - Steve</i>
            </b>
          </p>
          <p>To practise finding pitches by ear, try the <Link to="/training">Ear Trainer</Link>.
            It connects a heard pitch with a fretboard position, while this exercise starts from a note name.</p>
        </div>
      </Card>
      <Card>
        <BottomMenu />
      </Card>
      {soundEnabled && <NotePlayer ref={audio} onAudioStateChange={onAudioStateChange} />}
      {/* put some space in the bottom of the chart so you can scroll the ear trainer chart into view */}
      <div style={{ height: "100vh" }} />
    </div>
  );
};

export default FretboardPage;
